// Story 3.7d — organization dashboard page.
import { useParams } from 'react-router';
import { Alert, Button, Skeleton, Space, Typography } from 'antd';
import { useOne } from '@refinedev/core';
import { useTranslation } from 'react-i18next';
import { BidiText } from '@lebinatikra/rtl-utils';
import { useDashboardMetrics } from './use-dashboard-metrics';
import { DashboardCards } from './dashboard-cards';

interface OrganizationRow {
  id: string;
  name: string;
}

export function OrganizationDashboard() {
  const { t } = useTranslation();
  const { communityId, organizationId } = useParams<{
    communityId: string;
    organizationId: string;
  }>();
  const id = organizationId ?? '';

  const { data, isLoading, error, refetch } = useDashboardMetrics('organization', id);
  const organization = useOne<OrganizationRow>({
    resource: 'organizations',
    id,
    meta: { parentId: communityId },
    queryOptions: { enabled: Boolean(id) && Boolean(communityId) },
  });
  const name = organization.query.data?.data?.name ?? '';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3}>
        <BidiText>{t('dashboard.organization.title', { name })}</BidiText>
      </Typography.Title>

      {isLoading && (
        <div data-testid="dashboard-skeleton">
          <Skeleton active />
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={(error as Error).message}
          data-testid="dashboard-error"
          action={
            <Button size="small" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}

      {data && <DashboardCards metrics={data} />}
    </Space>
  );
}
